import React from 'react';
import { useParams } from 'react-router-dom';
import { Droplet, Heart, Shield, Home, Leaf, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const SlideDetail = () => {
  const { slug } = useParams();
  
  const slides = [
    {
      image: '/slides/slide2.jpg',
      title: 'Accès à l\'eau potable',
      slug: 'acces-a-leau-potable',
      icon: <Droplet className="h-8 w-8" />,
      gradient: 'from-blue-500 to-cyan-600',
      description: 'Nous facilitons l\'accès à une eau potable et saine pour tous en construisant des forages d\'eau.',
      fullDescription: `L'accès à une eau potable et saine est un droit fondamental et une priorité essentielle pour améliorer les conditions de vie des populations. Action-Humanitaire Bénin joue un rôle clé en facilitant cet accès grâce à des forages d'eau durables.

                    Défis liés à l'accès à l'eau potable
                    L'absence d'eau potable entraîne de graves conséquences sanitaires et socio-économiques, notamment :  
                    - La propagation de maladies hydriques (choléra, dysenterie, typhoïde).  
                    - La difficulté des enfants à suivre une scolarité normale faute d'hygiène adéquate.  
                    - La précarité des communautés agricoles et leur dépendance aux eaux non sécurisées.  

                    Solutions mises en place : Construction de forages
                    Action-Humanitaire Bénin met en œuvre des projets de construction de forages pour garantir un accès durable à l'eau potable. Les actions incluent :  
                    - Étude de terrain et identification des besoins des communautés ciblées.  
                    - Construction de forages équipés de pompes manuelles ou solaires pour assurer une distribution fiable.  
                    - Formation des communautés à la gestion et l'entretien des infrastructures hydrauliques.  
                    - Mise en place de comités locaux pour garantir la pérennité du projet et prévenir les défaillances techniques.  

                    Sensibilisation et impact social
                    En parallèle, des campagnes de sensibilisation sont menées sur :  
                    - L'importance du traitement de l'eau avant consommation.  
                    - Les bonnes pratiques d'hygiène et d'assainissement pour prévenir les infections.  
                    - La gestion responsable de la ressource pour éviter le gaspillage et assurer une répartition équitable.`
    },
    {
      image: '/slides/slide5.jpg',
      title: 'Intégration et amélioration de la qualité de vie des populations vulnérables',
      slug: 'integration-et-amelioration',
      icon: <Heart className="h-8 w-8" />,
      gradient: 'from-pink-500 to-purple-600',
      description: 'Nous œuvrons à intégrer et améliorer les conditions de vie des communautés locales',
      fullDescription: `L'intégration et l'amélioration de la qualité de vie des populations vulnérables est une approche globale qui vise à offrir des conditions dignes, inclusives et durables aux communautés les plus fragiles. Action-Humanitaire Bénin peut jouer un rôle clé à travers plusieurs initiatives structurées :

Inclusion sociale et autonomisation des populations
- Programmes d'insertion socio-professionnelle: Formation technique et soutien à l'entrepreneuriat pour favoriser l'indépendance économique.  
- Soutien aux groupes marginalisés: Accompagnement des femmes, des personnes handicapées et des jeunes défavorisés pour leur garantir un accès équitable aux ressources.  
- Création de coopératives solidaires: Encouragement des activités génératrices de revenus basées sur les compétences locales (artisanat, agriculture, services).  
- Appui aux victimes de crises et déplacés internes: Aide matérielle et psychosociale pour faciliter la réintégration.  

Amélioration des conditions de vie
- Accès au logement et à l'habitat décent: Construction ou réhabilitation de logements pour les familles vulnérables.  
- Développement des infrastructures communautaires: Espaces publics accessibles (centres de santé, écoles, bibliothèques).  
- Électrification des zones reculées: Solutions d'énergie renouvelable pour améliorer les conditions de vie et l'accès aux services essentiels.  

Promotion de la cohésion sociale et du vivre-ensemble
- Éducation civique et sensibilisation aux droits humains: Formations et plaidoyers pour favoriser une société plus inclusive et équitable.  
- Médiation et gestion des conflits communautaires: Mise en place de dialogues intercommunautaires pour renforcer la paix sociale.  
- Encouragement au bénévolat et à l'engagement citoyen: Mobilisation des jeunes et des volontaires dans des actions de solidarité.  

Stratégies de mise en œuvre
- Collaboration avec les acteurs locaux et internationaux: Mobilisation des institutions et partenaires pour garantir des financements et des expertises adaptées.  
- Suivi et évaluation des actions: Indicateurs d'impact pour mesurer l'efficacité des programmes et ajuster les interventions.  
- Approche participative: Implication directe des bénéficiaires dans la conception et la mise en œuvre des projets pour assurer leur appropriation.`
    },
    {
      image: '/slides/slide4.jpg',
      title: 'Santé et sécurité alimentaire',
      slug: 'sante-et-securite-alimentaire',
      icon: <Shield className="h-8 w-8" />,
      gradient: 'from-green-500 to-teal-600',
      description: 'Nous travaillons à garantir l\'accès aux soins de santé de base pour la population et à fournir des informations pour une bonne hygiène de vie.',
      fullDescription: `Accès aux soins de santé de base
                        Garantir des services de santé accessibles et de qualité implique plusieurs initiatives :  
                        - Renforcement des infrastructures sanitaires: Création et réhabilitation de centres de santé locaux.  
                        - Programmes de vaccination et prévention des maladies: Sensibilisation aux maladies infectieuses et accès aux vaccins.  
                        - Formation du personnel médical: Ateliers et formations pour les soignants locaux afin d'améliorer la prise en charge des patients.  
                        - Dispensation de soins gratuits ou à coûts réduits: Consultation médicale, suivi des femmes enceintes et des enfants, fourniture de médicaments essentiels.  
                        - Téléconsultation et médecine mobile: Utilisation de la technologie pour atteindre les zones reculées.  

                        Sécurité alimentaire et nutrition
                        Une alimentation saine et durable est indispensable pour le développement des communautés :  
                        - Promotion de l'agriculture locale: Soutien aux petits agriculteurs, formation sur les techniques agricoles résilientes.  
                        - Sensibilisation à la nutrition: Éducation sur les bonnes pratiques alimentaires pour lutter contre la malnutrition.  
                        - Distribution de denrées alimentaires: Mise en place de banques alimentaires et programmes de repas scolaires.  
                        - Lutte contre le gaspillage alimentaire: Réutilisation et redistribution des surplus alimentaires.  
                        - Encouragement à l'autosuffisance: Création de jardins communautaires et projets agroécologiques.  

                        Hygiène et prévention sanitaire
                        Une bonne hygiène est essentielle pour prévenir les maladies :  
                        - Accès à l'eau potable: Mise en place de forages et systèmes de purification d'eau.  
                        - Campagnes de sensibilisation: Ateliers sur l'hygiène corporelle, environnementale et alimentaire.  
                        - Distribution de kits sanitaires: Savons, désinfectants, matériel d'hygiène féminine.  
                        - Amélioration des systèmes d'assainissement: Construction de latrines écologiques et gestion des déchets.`
    },
    {
      image: '/slides/slide1.jpg',
      title: 'Moderniser le cadre de vie des populations défavorisées',
      slug: 'moderniser-le-cadre-de-vie',
      icon: <Home className="h-8 w-8" />,
      gradient: 'from-orange-500 to-red-600',
      description: 'Nous combattons activement les inégalités sociales pour une société plus juste et équitable, en passant par une bonne éducation',
      fullDescription: `Amélioration des infrastructures de base
            - Logement décent: Construction de logements sociaux durables et accessibles.  
            - Accès à l'eau et à l'assainissement: Déploiement de réseaux d'eau potable et de systèmes de gestion des déchets.  
            - Énergie propre : Promotion de solutions énergétiques renouvelables comme les panneaux solaires pour garantir l'autonomie des communautés.  

            Éducation comme levier contre les inégalités
            L'éducation est l'un des piliers fondamentaux pour réduire la pauvreté et créer une société plus juste.
            - Construction et réhabilitation d'écoles dans les zones défavorisées.  
            - Formation professionnelle adaptée aux besoins locaux pour favoriser l'employabilité.  
            - Accès à des ressources éducatives comme des bibliothèques et du matériel pédagogique numérique.  
            - Programmes de sensibilisation pour promouvoir l'éducation des filles et réduire les disparités de genre.  

            Inclusion sociale et développement économique
            - Appui aux micro-entrepreneurs et formations en gestion financière pour permettre aux familles de sortir du cycle de la pauvreté.  
            - Soutien aux coopératives locales qui valorisent le savoir-faire des communautés (artisanat, agriculture, etc.).  
            - Création d'espaces publics favorisant le bien-être collectif (parcs, centres communautaires).`
    },
    {
      image: '/slides/slide3.jpg',
      title: 'Sensibilisation et éducation sur les questions environnementales et sanitaires',
      slug: 'sensibilisation-et-education',
      icon: <Leaf className="h-8 w-8" />,
      gradient: 'from-emerald-500 to-green-600',
      description: 'Nous éduquons et sensibilisons les communautés aux enjeux environnementaux et sanitaires',
      fullDescription: `La sensibilisation et l'éducation sur les questions environnementales et sanitaires sont essentielles pour renforcer la résilience des communautés et favoriser des pratiques durables.

Sensibilisation aux enjeux environnementaux
L'objectif est d'amener les populations à comprendre l'impact de leurs actions sur l'environnement et à adopter des comportements responsables :
- Éducation sur le changement climatique: Explication des causes et conséquences, mise en avant des solutions locales.  
- Protection de la biodiversité: Sensibilisation à la conservation des écosystèmes et lutte contre la déforestation.  
- Gestion des déchets: Programmes de tri sélectif et promotion du recyclage pour réduire la pollution.  
- Énergie renouvelable et consommation responsable: Encouragement à l'utilisation de sources d'énergie durables et à la réduction des déchets plastiques.  
- Reboisement et agriculture écologique: Formation sur les techniques agroécologiques pour protéger les sols et garantir une production durable.  

Sensibilisation sanitaire et hygiène
L'éducation sanitaire permet de réduire les maladies évitables et d'améliorer la qualité de vie des communautés :
- Hygiène de l'eau et accès à l'assainissement: Enseignement des bonnes pratiques pour éviter les maladies hydriques.  
- Nutrition et prévention de la malnutrition: Sensibilisation à une alimentation équilibrée et à l'importance des micronutriments.  
- Lutte contre les maladies infectieuses: Campagnes d'information sur le paludisme, la tuberculose et les maladies liées à l'hygiène.  
- Santé maternelle et infantile: Éducation sur les soins prénataux et la vaccination des enfants.  
- Hygiène menstruelle et santé reproductive: Distribution de kits d'hygiène et sensibilisation à la santé des femmes.  

Méthodes de sensibilisation efficaces
Pour maximiser l'impact, il est essentiel d'adopter des approches interactives et inclusives:
- Ateliers communautaires avec démonstrations pratiques.  
- Utilisation des médias et réseaux sociaux pour toucher un large public.  
- Événements locaux et campagnes de proximité dans les écoles et centres de santé.  
- Implication des leaders communautaires pour une adoption rapide des bonnes pratiques.  
- Jeux éducatifs et contenus adaptés pour les enfants et les jeunes.`
    }
  ];

  const currentSlide = slides.find(slide => slide.slug === slug);

  if (!currentSlide) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#089556] mb-4">Page non trouvée</h1>
          <p className="text-gray-600 mb-8">Le contenu que vous recherchez n'existe pas.</p>
          <a href="/" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Enhanced Hero Section - Reduced Height */}
      <section className="relative h-[300px] mb-16 overflow-hidden">
        {/* Image Background */}
        <div className="absolute inset-0">
          <img
            src={currentSlide.image}
            alt={currentSlide.title}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Enhanced Overlay with Molecular Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#089556]/80 via-[#089556]/60 to-[#07D1EB]/80">
          <div className="absolute inset-0 opacity-[0.4]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 3px, transparent 3px),
                radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2px, transparent 2px),
                radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px)
              `,
              backgroundSize: '200px 200px, 160px 160px, 220px 220px, 180px 180px, 190px 190px'
            }}></div>
            
            {/* Enhanced Connection Lines */}
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(35deg, transparent 48%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, transparent 52%),
                linear-gradient(-35deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%)
              `,
              backgroundSize: '350px 350px, 320px 320px',
              opacity: '0.5'
            }}></div>
          </div>

          {/* Enhanced Floating Molecules */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/6 left-1/4 w-6 h-6 bg-white rounded-full opacity-20 animate-pulse shadow-xl shadow-white/50"></div>
            <div className="absolute top-2/3 right-1/4 w-4 h-4 bg-white rounded-full opacity-30 animate-bounce shadow-xl shadow-white/60"></div>
            <div className="absolute top-1/2 left-3/4 w-5 h-5 bg-white rounded-full opacity-25 animate-pulse shadow-xl shadow-white/55"></div>
            <div className="absolute top-1/4 right-1/3 w-3 h-3 bg-white rounded-full opacity-35 animate-bounce shadow-xl shadow-white/45"></div>
            <div className="absolute bottom-1/6 left-1/6 w-4 h-4 bg-white rounded-full opacity-25 animate-pulse shadow-xl shadow-white/50"></div>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10 max-w-5xl px-4">
            <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${currentSlide.gradient} rounded-full mb-6 shadow-xl border-4 border-white/30`}>
              <div className="text-white">{currentSlide.icon}</div>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-white tracking-tight drop-shadow-lg mb-4 leading-tight">
              {currentSlide.title}
            </h1>
            <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed drop-shadow-sm">
              {currentSlide.description}
            </p>
            <div className="w-24 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Enhanced Content Section */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="relative overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 30% 30%, #089556 2px, transparent 2px),
                radial-gradient(circle at 70% 70%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '160px 160px, 130px 130px'
            }}></div>
          </div>

          <div className="relative z-10 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20">
            <div className={`h-1 bg-gradient-to-r ${currentSlide.gradient}`}></div>
            
            <div className="p-8 lg:p-12">
              {/* Header Section */}
              <div className="flex items-center gap-6 mb-12">
                <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${currentSlide.gradient} rounded-full shadow-lg`}>
                  <div className="text-white">{currentSlide.icon}</div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#089556] mb-2">Détails du Projet</h2>
                  <div className={`w-16 h-1 bg-gradient-to-r ${currentSlide.gradient} rounded-full`}></div>
                </div>
              </div>

              {/* Content Parsing with Professional Design */}
              <div className="space-y-8">
                {currentSlide.fullDescription.split('\n\n').map((paragraph, index) => {
                  const trimmedParagraph = paragraph.trim();
                  
                  // Skip empty paragraphs
                  if (!trimmedParagraph) return null;
                  
                  const lines = trimmedParagraph.split('\n');
                  return (
                    <div key={index} className="mb-8 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                      {lines.map((line, lineIndex) => {
                        const trimmedLine = line.trim();
                        
                        // Skip empty lines
                        if (!trimmedLine) return null;
                        
                        // Check if it's a section title (no emoji, not starting with -, not indented)
                        if (!trimmedLine.startsWith('-') && !trimmedLine.includes(':') && lineIndex < lines.length - 1) {
                          return (
                            <div key={lineIndex} className={`p-6 bg-gradient-to-r ${currentSlide.gradient} text-white`}>
                              <h3 className="text-xl font-bold mb-2">
                                {trimmedLine}
                              </h3>
                              <div className="w-16 h-0.5 bg-white/60 rounded-full"></div>
                            </div>
                          );
                        }
                        
                        // Check if it's a list item
                        if (trimmedLine.startsWith('-')) {
                          const colonIndex = trimmedLine.indexOf(':');
                          if (colonIndex > 0) {
                            const title = trimmedLine.substring(1, colonIndex).trim();
                            const description = trimmedLine.substring(colonIndex + 1).trim();
                            return (
                              <div key={lineIndex} className="p-6 border-l-4 border-[#089556]/20 bg-gray-50/50">
                                <h4 className="font-semibold text-[#089556] text-lg mb-2">{title}</h4>
                                <p className="text-gray-700 leading-relaxed text-justify">{description}</p>
                              </div>
                            );
                          } else {
                            return (
                              <div key={lineIndex} className="p-6 border-l-4 border-[#089556]/20 bg-gray-50/50">
                                <p className="text-gray-700 leading-relaxed text-justify">
                                  {trimmedLine.substring(1).trim()}
                                </p>
                              </div>
                            );
                          }
                        }
                        
                        // Regular paragraph text
                        if (trimmedLine.length > 0) {
                          return (
                            <div key={lineIndex} className="p-6">
                              <p className="text-gray-800 text-lg leading-relaxed text-justify">
                                {trimmedLine}
                              </p>
                            </div>
                          );
                        }
                        
                        return null;
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Call to Action */}
              <div className="mt-12 p-8 bg-gradient-to-br from-[#089556]/15 via-[#089556]/10 to-[#07D1EB]/15 rounded-2xl shadow-lg border border-white/20 text-center">
                <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${currentSlide.gradient} rounded-full mb-6 shadow-lg`}>
                  <div className="text-white">{currentSlide.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-[#089556] mb-4">
                  Participez à Cette Initiative
                </h3>
                <p className="text-gray-700 text-lg max-w-2xl mx-auto leading-relaxed text-justify mb-8">
                  Votre soutien peut faire la différence dans la réalisation de ce projet. Rejoignez-nous pour créer un impact durable dans la vie des communautés bénéficiaires.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button className={`inline-flex items-center px-8 py-3 bg-gradient-to-r ${currentSlide.gradient} text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300`}>
                    <div className="mr-3">{currentSlide.icon}</div>
                    Soutenir ce projet
                  </button>
                  <button className="inline-flex items-center px-8 py-3 bg-white/80 backdrop-blur-sm text-[#089556] font-semibold rounded-xl border-2 border-[#089556]/20 hover:bg-[#089556]/10 hover:scale-105 transition-all duration-300">
                    <Heart className="w-5 h-5 mr-3" />
                    En savoir plus
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SlideDetail;
